import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Contract } from 'ethers';

import useFiboChainRpc from '@/store/builtInRpc/useFiboChainRpc';

import useDebounceFn from './useDebounceFn';

/**
 * 获取报价 Hook（防抖处理）
 * @param lpContract LP 合约实例（需提前创建）
 * @param TokenA     Token A 地址（输入 token）
 * @param TokenB     Token B 地址（输出 token）
 * @param tokenInput 输入 TokenA 的数量（单位为最小单位，如 wei）
 * 
 * 注意：此函数不会判断 LP 是否存在，使用前应先验证 LP 是否有效。
 * 
 * @returns {
 *   quoteLod: boolean - 是否加载中
 *   amountOut: bigint - 兑换输出数量
 *   getQuote: () => void - 主动触发查询
 * }
 */
const useQuote = (
    lpContract: Contract | null,
    TokenA: string,
    TokenB: string,
    tokenInput: bigint
) => {
    // loading 状态
    const [quoteLod, setQuoteLoading] = useState(false);
    // 兑换输出数量
    const [amountOut, setAmountOut] = useState(BigInt(0));
    // 获取只读路由合约实例
    const { routerAbiReadOnly } = useFiboChainRpc();

    // 请求唯一 ID（用于避免竞态，保证只处理最后一次请求结果）
    const requestIdRef = useRef(0);

    /**
     * 获取报价的主函数
     * 只处理最后一次请求的结果，避免竞态
     */
    const getQuote = useCallback(async () => {
        const currentRequestId = ++requestIdRef.current;

        if (routerAbiReadOnly && lpContract) {
            try {
                // 输入为 0 时直接返回 0
                if (tokenInput === BigInt(0)) {
                    if (requestIdRef.current === currentRequestId) {
                        setAmountOut(BigInt(0));
                    }
                    return;
                }

                setQuoteLoading(true);

                // 获取 LP 储备
                const reserves = await lpContract.getReserves();
                // 判断 TokenA/TokenB 顺序
                const isSorted = TokenA.toLowerCase() < TokenB.toLowerCase();
                const reserveA = isSorted ? reserves[0] : reserves[1];
                const reserveB = isSorted ? reserves[1] : reserves[0];

                // 调用合约方法获取报价
                const out = await routerAbiReadOnly.quote(tokenInput, reserveA, reserveB);

                // 只处理最后一次请求的结果
                if (requestIdRef.current === currentRequestId) {
                    setAmountOut(out);
                }
            } catch (error) {
                // 捕获异常，打印错误日志
                console.log('useQuote error', error);
            } finally {
                // 只处理最后一次请求的 loading 状态
                if (requestIdRef.current === currentRequestId) {
                    setQuoteLoading(false);
                }
            }
        } else {
            // 参数不全时，输出金额为 0
            if (requestIdRef.current === currentRequestId) {
                setAmountOut(BigInt(0));
            }
        }
    }, [TokenA, TokenB, lpContract, routerAbiReadOnly, tokenInput]);

    // 防抖处理，避免频繁请求
    const getAmountOutDebounceFns = useDebounceFn(getQuote, 300);

    // 依赖变化时自动发起查询
    useEffect(() => {
        getAmountOutDebounceFns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getQuote]);

    return {
        quoteLod,    // 是否加载中
        amountOut,   // 兑换输出数量
        getQuote,    // 主动触发查询
    };
};

export default useQuote;